import type { EnhancedDeal, TaskGroup, TimberProject } from '../types';
import { projectHasArchitectUid } from './architectProjectAccess';
import { isDesignGroupName } from './designGroupName';

export type ArchitectPortalDeepLinkFailureReason =
  | 'not_found'
  | 'no_access'
  | 'completed'
  | 'no_design_group';

export type ArchitectPortalDeepLinkResolution =
  | { status: 'idle' }
  | { status: 'pending' }
  | {
      status: 'ready';
      project: TimberProject;
      designGroup: TaskGroup;
      commentId: string | null;
    }
  | {
      status: 'unavailable';
      reason: ArchitectPortalDeepLinkFailureReason;
      message: string;
    };

const UNAVAILABLE_MESSAGES: Record<ArchitectPortalDeepLinkFailureReason, string> = {
  not_found: 'This discussion link points to a project that could not be found.',
  no_access:
    'You do not have access to this project’s Design discussion. Ask LCTF to grant you on the job if you need to reply.',
  completed:
    'This project is marked completed, so its Design discussion is no longer available from the architect portal.',
  no_design_group:
    'This project does not have a Design discussion yet. Ask LCTF if you expected to review drawings here.',
};

/**
 * Resolve `?project=&taskComment=` on `/architect-portal` without consuming the
 * URL. Callers must wait until the **architect hub** project list (same
 * `timberProjects` the Status cards use) and Design taskGroups are ready before
 * treating `unavailable` as final, and should only strip query params after
 * successfully opening the discussion.
 *
 * Important: for role `architect`, CRM `loading` goes false while
 * `timberProjects` is still `[]` — hub docs load later from
 * `users.architectHubProjectIds` (often after a cache-only empty user snap).
 * Pass `architectHubReady` from CRMContext (server user doc + all project
 * snaps), never `!crmLoading`. Callers should not strip URL params on
 * `not_found` so a late hub fill can still open the discussion.
 */
export function resolveArchitectPortalDiscussionDeepLink(input: {
  projectId: string | null | undefined;
  commentId?: string | null | undefined;
  architectUid: string | null | undefined;
  /** Same array ExternalArchitectPortal filters into the Status project list. */
  timberProjects: TimberProject[];
  enhancedDeals: EnhancedDeal[];
  taskGroups: TaskGroup[];
  /**
   * True only after the hub project list has finished its first hydrate
   * (architect: `architectHubReady`; staff preview: CRM `!loading`).
   * Empty `timberProjects` + ready ⇒ genuine not_found; empty + !ready ⇒ pending.
   */
  projectsReady: boolean;
  /** Lazy / hub `taskGroups` first snapshot has arrived. */
  taskGroupsReady: boolean;
}): ArchitectPortalDeepLinkResolution {
  const projectId = (input.projectId || '').trim();
  if (!projectId) return { status: 'idle' };

  // Never treat an empty list as not_found while the hub is still loading.
  if (!input.projectsReady) return { status: 'pending' };

  const project = input.timberProjects.find((p) => p.id === projectId);
  if (!project) {
    return {
      status: 'unavailable',
      reason: 'not_found',
      message: UNAVAILABLE_MESSAGES.not_found,
    };
  }

  if (project.status === 'completed') {
    return {
      status: 'unavailable',
      reason: 'completed',
      message: UNAVAILABLE_MESSAGES.completed,
    };
  }

  const deal = project.dealId
    ? input.enhancedDeals.find((d) => d.id === project.dealId)
    : undefined;
  const uid = (input.architectUid || '').trim();
  // Staff portal preview without a selected architect cannot assert access yet.
  if (uid && !projectHasArchitectUid(project, deal, uid)) {
    return {
      status: 'unavailable',
      reason: 'no_access',
      message: UNAVAILABLE_MESSAGES.no_access,
    };
  }

  if (!input.taskGroupsReady) return { status: 'pending' };

  const designGroup = input.taskGroups.find(
    (g) => g.projectId === project.id && isDesignGroupName(g.name),
  );
  if (!designGroup) {
    return {
      status: 'unavailable',
      reason: 'no_design_group',
      message: UNAVAILABLE_MESSAGES.no_design_group,
    };
  }

  const commentId = (input.commentId || '').trim() || null;
  return { status: 'ready', project, designGroup, commentId };
}

/** Designer (and similar) alias of an architect-portal discussion deep link. */
export function buildDesignerArchitectDiscussionHubPath(input: {
  projectId: string;
  commentId?: string | null;
}): string {
  const projectId = input.projectId.trim();
  const q = new URLSearchParams();
  q.set('discussion', 'architect');
  const commentId = (input.commentId || '').trim();
  if (commentId) q.set('taskComment', commentId);
  return `/project-hub/${projectId}?${q.toString()}`;
}
